import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Card,
  Button,
  Alert,
  Skeleton,
  message,
  Modal,
  Form,
  Input,
} from 'antd';
import {
  ReloadOutlined,
  EditOutlined,
  CalendarOutlined,
  StopOutlined,
  CheckCircleOutlined,
  SafetyOutlined,
  UnlockOutlined,
} from '@ant-design/icons';
import {
  getStoredNote,
  saveNote,
  resetUserPassword,
  updateUserShadowban,
  unlockUserCod,
} from '../../services/userManagementService';
import { UserDetailTabs, type UserDetailTabKey } from './UserDetailTabs';
import { OverviewTab } from './tabs/OverviewTab';
import { OrderHistoryTab } from './tabs/OrderHistoryTab';
import { LoyaltyPointsTab } from './tabs/LoyaltyPointsTab';
import { ActivityLogTab } from './tabs/ActivityLogTab';
import { useUserOverview } from '../../hooks/useUserOverview';
import { useUpdateUserStatus } from '../../hooks/useUpdateUserStatus';
import { EditProfileModal } from './EditProfileModal';
import { formatVnDate } from '../../utils/vnDateTime';

function getInitials(name: string | undefined | null): string {
  if (!name || !name.trim()) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2)
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

const AVATAR_COLORS = ['#ea580c', '#2563eb', '#7c3aed', '#047857', '#db2777'];
function avatarColor(name: string | undefined | null): string {
  if (!name) return AVATAR_COLORS[0];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h << 5) - h + name.charCodeAt(i);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

export default function UserDetails() {
  const { userId } = useParams<{ userId: string }>();
  const [activeTab, setActiveTab] = useState<UserDetailTabKey>('overview');
  const [noteValue, setNoteValue] = useState('');
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [shadowbanSaving, setShadowbanSaving] = useState(false);
  const [unlockCodSaving, setUnlockCodSaving] = useState(false);
  const [passwordForm] = Form.useForm<{
    newPassword: string;
    confirmPassword: string;
  }>();

  const {
    data: overview,
    isLoading: loadingOverview,
    isError: overviewError,
    refetch: refetchOverview,
  } = useUserOverview(userId ?? undefined);

  const updateStatus = useUpdateUserStatus(userId, {
    onSuccess: () => refetchOverview(),
  });

  useEffect(() => {
    if (!userId) return;
    setNoteValue(getStoredNote(userId));
  }, [userId]);

  const handleSaveNote = () => {
    if (userId) {
      saveNote(userId, noteValue);
      message.success('Note saved');
    }
  };

  const openResetPasswordModal = () => {
    passwordForm.resetFields();
    setPasswordModalOpen(true);
  };

  const handleResetPasswordSubmit = async () => {
    if (!userId) return;
    try {
      const values = await passwordForm.validateFields();
      if (values.newPassword !== values.confirmPassword) {
        message.error('Passwords do not match');
        return;
      }
      setPasswordSaving(true);
      await resetUserPassword(userId, values.newPassword);
      message.success('Password has been reset');
      setPasswordModalOpen(false);
      passwordForm.resetFields();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } } };
      if (ax.response?.data?.message) {
        message.error(ax.response.data.message);
      } else if (err instanceof Error && err.message) {
        message.error(err.message);
      }
    } finally {
      setPasswordSaving(false);
    }
  };

  const profile = overview?.profile;
  const loyalty = overview?.loyalty;
  const displayName = profile?.name ?? 'User';
  const isActive = profile?.status === 'active';
  const isShadowbanned = Boolean(profile?.isShadowbanned);
  const isCodRestricted = Boolean(profile?.codRestricted);

  const handleUnlockCodClick = () => {
    if (!userId) return;
    Modal.confirm({
      title: 'Mở khóa COD?',
      content:
        'User sẽ có thể thanh toán COD lại ngay. Hành động này xóa cod_restricted_until và cod_restriction_reason.',
      okText: 'Mở khóa COD',
      cancelText: 'Cancel',
      onOk: async () => {
        setUnlockCodSaving(true);
        try {
          const res = await unlockUserCod(userId);
          if (res.success) {
            message.success(res.message ?? 'Đã mở khóa COD');
            refetchOverview();
          } else {
            message.error('Mở khóa COD thất bại');
          }
        } catch {
          message.error('Mở khóa COD thất bại');
        } finally {
          setUnlockCodSaving(false);
        }
      },
    });
  };

  const handleShadowbanClick = () => {
    if (!userId) return;
    const next = !isShadowbanned;
    Modal.confirm({
      title: next ? 'Shadowban user (ẩn quảng cáo)?' : 'Gỡ shadowban?',
      content: next
        ? 'User vẫn có thể đặt hàng nhưng sẽ không thấy quảng cáo sponsored.'
        : 'User sẽ thấy lại quảng cáo sponsored.',
      okText: next ? 'Shadowban' : 'Gỡ shadowban',
      cancelText: 'Cancel',
      okButtonProps: next ? { danger: true } : undefined,
      onOk: async () => {
        setShadowbanSaving(true);
        try {
          const res = await updateUserShadowban(userId, next);
          if (res.success) {
            message.success(res.message ?? 'Cập nhật thành công');
            refetchOverview();
          } else {
            message.error('Cập nhật thất bại');
          }
        } catch {
          message.error('Cập nhật thất bại');
        } finally {
          setShadowbanSaving(false);
        }
      },
    });
  };

  const handleStatusClick = () => {
    const newStatus = isActive ? 'INACTIVE' : 'ACTIVE';
    const title = isActive ? 'Inactive user?' : 'Activate user?';
    const desc = isActive
      ? 'User sẽ không thể đăng nhập/đặt hàng khi bị inactive.'
      : 'User sẽ có thể đăng nhập và đặt hàng trở lại.';
    Modal.confirm({
      title,
      content: desc,
      okText: isActive ? 'Inactive' : 'Activate',
      cancelText: 'Cancel',
      onOk: () => updateStatus.mutate(newStatus),
    });
  };

  const tabItems = [
    {
      key: 'overview' as const,
      label: 'Overview',
      children: (
        <OverviewTab
          overview={overview}
          loading={loadingOverview}
          error={overviewError}
          onRetry={refetchOverview}
          onViewPointsHistory={() => setActiveTab('loyalty')}
          noteValue={noteValue}
          onNoteChange={setNoteValue}
        />
      ),
    },
    {
      key: 'orders' as const,
      label: 'Order History',
      children: (
        <OrderHistoryTab
          userId={userId ?? ''}
          loyaltyBalance={loyalty?.currentPoints ?? 0}
          loyaltyLifetime={loyalty?.lifetimeEarned ?? 0}
          loyaltyExpiring={loyalty?.expiringSoon ?? 0}
          loyaltyGoal={loyalty?.nextTierGoal ?? 5000}
          noteValue={noteValue}
          onNoteChange={setNoteValue}
          onSaveNote={handleSaveNote}
        />
      ),
    },
    {
      key: 'loyalty' as const,
      label: 'Loyalty Points',
      children: (
        <LoyaltyPointsTab
          userId={userId ?? ''}
          loyaltySummary={{
            currentBalance: loyalty?.currentPoints ?? 0,
            lifetimeEarned: loyalty?.lifetimeEarned ?? 0,
            expiringSoon: loyalty?.expiringSoon ?? 0,
            tier: loyalty?.tier ?? 'Bronze',
            goal: loyalty?.nextTierGoal ?? 500,
          }}
          noteValue={noteValue}
          onNoteChange={setNoteValue}
          onSaveNote={handleSaveNote}
        />
      ),
    },
    {
      key: 'activity' as const,
      label: 'Activity Log',
      children: (
        <ActivityLogTab
          userId={userId ?? ''}
          noteValue={noteValue}
          onNoteChange={setNoteValue}
          onSaveNote={handleSaveNote}
        />
      ),
    },
  ];

  if (overviewError && !profile) {
    return (
      <div className="merchant-detail-page">
        <header className="merchant-detail-header">
          <h1 className="merchant-detail-title">User Details</h1>
          <nav className="merchant-detail-breadcrumb">
            <Link to="/">Dashboard</Link>
            <span className="merchant-detail-breadcrumb-sep">&gt;</span>
            <Link to="/user-management">User Management</Link>
            <span className="merchant-detail-breadcrumb-sep">&gt;</span>
            <span className="merchant-detail-breadcrumb-current">–</span>
          </nav>
        </header>
        <Alert
          type="error"
          message="Không thể tải dữ liệu người dùng. Vui lòng thử lại."
          showIcon
          closable
          action={
            <Button size="small" onClick={() => refetchOverview()}>
              Thử lại
            </Button>
          }
        />
      </div>
    );
  }

  if (loadingOverview && !profile) {
    return (
      <div className="merchant-detail-page">
        <header className="merchant-detail-header">
          <h1 className="merchant-detail-title">User Details</h1>
          <nav className="merchant-detail-breadcrumb">
            <Link to="/">Dashboard</Link>
            <span className="merchant-detail-breadcrumb-sep">&gt;</span>
            <Link to="/user-management">User Management</Link>
          </nav>
        </header>
        <Card className="merchant-detail-header-card">
          <Skeleton active avatar paragraph={{ rows: 2 }} />
        </Card>
        <div className="merchant-detail-tabs">
          <Skeleton active paragraph={{ rows: 6 }} />
        </div>
      </div>
    );
  }

  return (
    <div className="merchant-detail-page">
      <header className="merchant-detail-header">
        <h1 className="merchant-detail-title">User Management</h1>
        <nav className="merchant-detail-breadcrumb">
          <Link to="/">Dashboard</Link>
          <span className="merchant-detail-breadcrumb-sep">&gt;</span>
          <Link to="/user-management">User Management</Link>
          <span className="merchant-detail-breadcrumb-sep">&gt;</span>
          <span className="merchant-detail-breadcrumb-current">
            User Details
          </span>
        </nav>
      </header>

      <Card className="merchant-detail-header-card">
        <div className="merchant-detail-header-card-body">
          <div className="merchant-detail-header-card-left">
            <div
              className="merchant-detail-header-logo"
              style={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                backgroundColor: avatarColor(displayName),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 24,
                fontWeight: 700,
                color: '#fff',
              }}
            >
              {getInitials(displayName)}
            </div>
            <div className="merchant-detail-header-info">
              <h2
                className="merchant-detail-header-name"
                style={{ display: 'flex', alignItems: 'center', gap: 10 }}
              >
                {displayName}
                <span
                  style={{ fontSize: 14, color: '#6b7280', fontWeight: 400 }}
                >
                  ID: #{profile?.code ?? `USR-${profile?.id ?? userId}`}
                </span>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '4px 10px',
                    borderRadius: 9999,
                    fontSize: 12,
                    fontWeight: 500,
                    background: isActive ? '#dcfce7' : '#f3f4f6',
                    color: isActive ? '#166534' : '#4b5563',
                  }}
                >
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      background: isActive ? '#22c55e' : '#9ca3af',
                    }}
                  />
                  {isActive ? 'Active' : 'Inactive'}
                </span>
                {isShadowbanned && (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '4px 10px',
                      borderRadius: 9999,
                      fontSize: 12,
                      fontWeight: 500,
                      background: '#fef3c7',
                      color: '#92400e',
                    }}
                  >
                    <SafetyOutlined />
                    Ads shadowban
                  </span>
                )}
                {isCodRestricted && (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '4px 10px',
                      borderRadius: 9999,
                      fontSize: 12,
                      fontWeight: 500,
                      background: '#fee2e2',
                      color: '#b91c1c',
                    }}
                  >
                    <StopOutlined />
                    COD blocked
                  </span>
                )}
              </h2>
              <div className="merchant-detail-header-meta">
                <span className="merchant-detail-header-meta-item">
                  <CalendarOutlined className="merchant-detail-header-meta-icon" />
                  {profile?.joinedAt
                    ? `Joined ${formatVnDate(profile.joinedAt, 'MMM D, YYYY')}`
                    : '–'}
                </span>
              </div>
            </div>
          </div>
          <div className="merchant-detail-header-card-actions">
            {isCodRestricted && (
              <Button
                icon={<UnlockOutlined />}
                onClick={handleUnlockCodClick}
                loading={unlockCodSaving}
                type="primary"
                className="merchant-detail-btn-suspend"
              >
                Mở khóa COD
              </Button>
            )}
            <Button
              icon={<SafetyOutlined />}
              onClick={handleShadowbanClick}
              loading={shadowbanSaving}
              danger={!isShadowbanned}
              className="merchant-detail-btn-suspend"
            >
              {isShadowbanned ? 'Gỡ shadowban QC' : 'Shadowban QC'}
            </Button>
            <Button
              icon={isActive ? <StopOutlined /> : <CheckCircleOutlined />}
              onClick={handleStatusClick}
              loading={updateStatus.isPending}
              className="merchant-detail-btn-suspend"
            >
              {isActive ? 'Inactive' : 'Activate'}
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={openResetPasswordModal}
              className="merchant-detail-btn-suspend"
            >
              Reset Password
            </Button>
            <Button
              type="primary"
              icon={<EditOutlined />}
              className="merchant-detail-btn-edit"
              onClick={() => setEditProfileOpen(true)}
            >
              Edit Profile
            </Button>
          </div>
        </div>
      </Card>

      <UserDetailTabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
      />

      <EditProfileModal
        open={editProfileOpen}
        onClose={() => setEditProfileOpen(false)}
        userId={userId}
        profile={profile}
        onSuccess={() => refetchOverview()}
      />

      <Modal
        title="Reset Password"
        open={passwordModalOpen}
        onCancel={() => setPasswordModalOpen(false)}
        onOk={handleResetPasswordSubmit}
        confirmLoading={passwordSaving}
        destroyOnClose
        okText="Reset Password"
      >
        <p style={{ marginBottom: 16, color: '#6b7280' }}>
          Set a new password for this user. They can sign in with it
          immediately.
        </p>
        <Form form={passwordForm} layout="vertical">
          <Form.Item
            name="newPassword"
            label="New Password"
            rules={[
              { required: true, message: 'Please enter new password' },
              { min: 6, message: 'Password must be at least 6 characters' },
            ]}
          >
            <Input.Password placeholder="New password" />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label="Confirm Password"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'Please confirm password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="Confirm new password" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
